/**
 * @file FormWrapper.tsx
 *
 * @fileoverview Wraps form containing GDPR consents.
 * Handles loading state and querying GDPR consents.
 */

import { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useGdprConsentContext } from '~/jobiqo/components/GDPRConsent/hooks/useGdprConsentContext';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { GdprConsentPlacement } from '~/jobiqo/types/types';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { LoadingCircleIcon } from '~/vcl/icons/LoadingCircle';

interface GDPRConsentFormWrapperProps {
  /**
   * Children node.
   */
  children: React.ReactNode;
  /**
   * GDPR consent placement
   */
  consentPlacement: GdprConsentPlacement;
}

export const GDPRConsentFormWrapper = ({
  consentPlacement,
  children,
}: GDPRConsentFormWrapperProps) => {
  const intl = useIntl();
  const {
      consentsAvailable,
      queryGdprUserConsent,
      queryGdprUserConsents,
    } = useGdprConsentContext(),
    { user }: UserContextInterface = useContext(UserContext);

  useEffect(() => {
    queryGdprUserConsent(consentPlacement);
    user?.email && queryGdprUserConsents(user.email, consentPlacement);
  }, []);

  return consentsAvailable ? (
    <>{children}</>
  ) : (
    <FlexItems justifyContent="center">
      <LoadingCircleIcon
        color="primary"
        height="140"
        width="140"
        title={intl.formatMessage({
          id: 'loading_message',
          defaultMessage: 'Loading',
        })}
      />
    </FlexItems>
  );
};

export default GDPRConsentFormWrapper;
